import React, { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { Col, Flex, Spacer } from '@amzn/stencil-react-components/layout';
import { Button } from '@amzn/stencil-react-components/button';
import { H1, Label, Text } from '@amzn/stencil-react-components/text';
import { EmailReportWidget } from './shared/EmailReportWidget';
import { Select } from '@amzn/stencil-react-components/form';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import {
  TableElement,
  TbodyElement,
  TdElement,
  TheadElement,
  TrElement,
} from '@amzn/stencil-react-components/table';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useGlobalContext, useUserDataContext } from '../hooks';
import { AuditItem } from '../types/backendService';

export const AuditReport = ({}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auditItems } = useUserDataContext();

  const auditMonthAggregationMap = new Map<string, AuditItem[]>();

  const monthLookback = 4; // TO DO: UPDATE WITH FINAL LOOKBACK
  let currentMonth = moment();
  for (let i = 0; i < monthLookback; i++) {
    auditMonthAggregationMap.set(currentMonth.format('MMM YYYY'), []);
    currentMonth = currentMonth.subtract(1, 'month');
  }

  if (auditItems) {
    auditItems.forEach((item) => {
      const month = moment(item.dateSubmitted! * 1000).format('MMM YYYY');
      if (auditMonthAggregationMap.has(month)) {
        auditMonthAggregationMap.get(month)!.push(item);
      }
    });
  }

  const startingMonth = moment().format('MMM YYYY');
  const startingItems = auditMonthAggregationMap.get(startingMonth) ?? [];
  const [selectedMonth, setSelectedMonth] = useState(startingMonth);
  const [auditMonthlyItems, setAuditMonthlyItems] = useState<AuditItem[]>(startingItems);

  const updateState = (month: string) => {
    setSelectedMonth(month);
    setAuditMonthlyItems(auditMonthAggregationMap.get(month) || []);
  };

  const tableRows = useMemo(() => {
    if (!auditMonthlyItems) return [];
    return auditMonthlyItems.map((item, index) => (
      <TrElement key={index}>
        <TdElement>{moment(item.dateSubmitted! * 1000).format('M/D/Y')}</TdElement>
        <TdElement>{item.performedBy}</TdElement>
        <TdElement>{item.auditType}</TdElement>
        <TdElement>{item.result}</TdElement>
      </TrElement>
    ));
  }, [auditMonthlyItems, auditItems]);

  if (auditItems === undefined) {
    return (
      <Flex height="188px" flexDirection="row" justifyContent="center" alignItems="center">
        <Spinner showText />
      </Flex>
    );
  }

  return (
    <>
      <Flex flexDirection="row" justifyContent="space-between">
        <H1>{t('auditTitle')}</H1>
        <Button onClick={() => navigate('/')}>{t('home')}</Button>
      </Flex>
      <Spacer height={16} />
      <Col gridGap="S200">
        <Flex flexDirection="row" justifyContent="right" alignItems="center" gridGap="S300">
          <Col alignItems="start" gridGap="S300">
            <Label htmlFor="month-selector" id="month-selector-label">
              <Text fontSize="T200" fontWeight="light" textAlign="right">
                {t('month')}
              </Text>
            </Label>
          </Col>
          <Col width={150}>
            <Select
              labelId="month-selector-label"
              id="month-selector"
              value={selectedMonth}
              onChange={(value) => updateState(value)}
              options={Array.from(auditMonthAggregationMap.keys())}
            />
          </Col>
          {/* <Col>
            <EmailReportWidget reportType='AuditReport' />
          </Col> */}
        </Flex>
        {auditMonthlyItems.length === 0 ? (
          <Text fontWeight="light">{t('noAuditData')}</Text>
        ) : (
          <Col>
            <TableElement>
              <TheadElement>
                <TrElement>
                  <TdElement>Date Submitted</TdElement>
                  <TdElement>Performed By</TdElement>
                  <TdElement>Audit Type</TdElement>
                  <TdElement>Result</TdElement>
                </TrElement>
              </TheadElement>
              <TbodyElement>{tableRows}</TbodyElement>
            </TableElement>
          </Col>
        )}
      </Col>
    </>
  );
};
